import type { StringKey } from "../contract/strings";

/**
 * The message Home shows when a create or a join is refused.
 *
 * Mirrors the native hosts' mapping (#597): a refusal must never look like a meeting
 * that started, and the reason must be the same sentence on every client. The
 * rate-limit answer carries the server's `Retry-After`, so the copy can say when to
 * come back instead of leaving the person to guess.
 */
export interface SessionStartFailure {
  key: StringKey;
  vars: Record<string, string | number>;
}

/** The minutes a `Retry-After` of `seconds` rounds up to, never below one. */
export function retryAfterMinutes(seconds: number): number {
  return Math.max(1, Math.ceil(seconds / 60));
}

/**
 * What `getUserMedia` throws when the microphone is refused, missing or held by something
 * else, plus the engine's own `CaptureStartError` for a microphone that opened and sent no
 * audio (#658). Each one is the person's to fix, so it is named rather than reported as a
 * connection problem.
 */
const MIC_FAILURES = new Set([
  "NotAllowedError",
  "NotFoundError",
  "NotReadableError",
  "SecurityError",
  "OverconstrainedError",
  "CaptureStartError",
]);

/**
 * A create refused for billing (#680, #686), by the stable `code` in its body -- never by
 * the status alone, since a 402 or 409 can mean other things.
 */
const BILLING_REFUSALS: Record<string, StringKey> = {
  billing_balance_exhausted: "home.error_start_no_time",
  billing_account_blocked: "home.error_start_paused",
  billing_meeting_in_progress: "home.error_start_other_meeting",
};

export function sessionStartFailure(error: unknown, isCreate: boolean): SessionStartFailure {
  const name = (error as { name?: unknown } | null)?.name;
  if (typeof name === "string" && MIC_FAILURES.has(name)) return { key: "home.error_mic", vars: {} };
  const code = (error as { data?: { code?: unknown } } | null)?.data?.code;
  if (isCreate && typeof code === "string" && Object.hasOwn(BILLING_REFUSALS, code)) {
    return { key: BILLING_REFUSALS[code], vars: {} };
  }
  const fallback: StringKey = isCreate ? "home.error_start" : "home.error_join";
  const limited: StringKey = isCreate ? "home.error_start_limited" : "home.error_join_limited";
  const failure = error as { status?: unknown; retryAfter?: unknown } | null;
  const retryAfter = failure?.retryAfter;
  if (
    failure?.status === 429 &&
    typeof retryAfter === "number" &&
    Number.isFinite(retryAfter) &&
    retryAfter > 0
  ) {
    return { key: limited, vars: { count: retryAfterMinutes(retryAfter) } };
  }
  return { key: fallback, vars: {} };
}
